/** 应用页面（路由语义） */
export type PageId = 'home' | 'session'

/** 素材墙 Tab */
export type MaterialTabKey = 'hot' | 'policy' | 'edu' | 'product' | 'more' | 'my-videos'

export interface GenParams {
  ratio: string
  resolution: string
  duration: string
}

export type RoundStatus =
  | 'pending'
  | 'analyzing'
  | 'specify_target'
  | 'not_video'
  | 'succeeded'
  | 'failed'
  | 'cancelled'

export const STATUS_MESSAGES: Record<'specify_target' | 'not_video', string> = {
  specify_target:
    '暂不支持指定修改某个历史视频，当前仅支持基于最近一次生成的视频进行修改或重新生成，请直接描述你想要调整的内容~',
  not_video: '我是视频生成助手，请描述你想要的视频画面内容，我来帮你生成~',
}

export function assistantMessageForStatus(status: RoundStatus): string | undefined {
  if (status === 'specify_target' || status === 'not_video') return STATUS_MESSAGES[status]
  return undefined
}

/** CDN 转存状态：空串表示未开始或进行中 */
export type CdnStatus = '' | 'uploading' | 'done' | 'failed'

export interface ConversationRound {
  id: string
  prompt: string
  actualPrompt?: string
  params: GenParams
  taskId: string
  videoUrl: string | null
  coverUrl?: string | null
  status: RoundStatus
  assistantMessage?: string
  imagesUsed?: string[]
  videosUsed?: string[]
  audiosUsed?: string[]
  model?: string
  watermark?: boolean
  statusLabel?: string
  progress?: number
  intent?: string
  referenceVideoUrl?: string
  createdAt?: string
  /** 埋点：本轮生成开始时间戳 */
  genStartedAt?: number
  /** 埋点：生成场景 */
  genReportType?: 'generate' | 'generate_again' | 'edit_again'
  /** CDN 转存状态 */
  cdnStatus?: CdnStatus
  /** 模型降级状态：空 / attempted / succeeded / failed */
  downgrade_status?: string
  /** 降级说明（用户可见） */
  downgrade_notice?: string
}

export interface HistoryItem {
  id: string
  prompt: string
  params: GenParams
  taskId: string
  videoUrl: string | null
  status: RoundStatus
  createdAt: string
}

export interface DetailVideo {
  src: string
  media: Array<{ type: 'image' | 'video'; src: string; label: string }>
  author: string
  avatar: string
  duration: string
  prompt: string
}

export interface PexelsVideoFile {
  quality: string
  link: string
}

export interface PexelsVideoPicture {
  picture?: string
}

/** Pexels 列表项（前端仅用部分字段） */
export interface PexelsVideo {
  id?: number
  demo_id?: string
  image?: string
  prompt_text?: string
  video_pictures?: PexelsVideoPicture[]
  video_files?: PexelsVideoFile[]
  user?: { name?: string }
  duration?: number
}

export interface SuggestionItem {
  keywords: string[]
  suffix: string
}

export interface StyleOption {
  key: string
  label: string
  icon: string
  bg: string
}

export interface RatioOption {
  value: string
  boxW: number
  boxH: number
}

export interface CameraOption {
  key: string
  label: string
  icon: string
}

export interface VideoModelOption {
  key: string
  label: string
  desc: string
}

export interface UploadedImage {
  id: string
  name: string
  /** 本地预览：blob URL 或 data URL */
  data: string
  /** 上传成功后的 CDN URL */
  cdnUrl?: string
  /** 原始文件，用于上传 */
  file?: File
  uploading?: boolean
  uploadError?: boolean
}

export interface UploadedVideo {
  id: string
  name: string
  /** 本地预览 blob URL */
  data: string
  cdnUrl?: string
  file?: File
  duration?: number
}

export interface UploadedAudio {
  id: string
  name: string
  data: string
  cdnUrl?: string
  file?: File
  duration?: number
}

export interface InputVideo {
  video_url: string
}

export interface UploadProgress {
  phase: 'idle' | 'uploading'
  total: number
  completed: number
}

export interface QuotaInfo {
  total: number
  used: number
  hold: number
  available: number
}

export interface ActivityCheckResult {
  eligible: boolean
  message: string
}

export interface AppStoreState {
  currentPage: PageId
  uploadedImages: UploadedImage[]
  uploadedVideos: UploadedVideo[]
  uploadedAudios: UploadedAudio[]
  /** @deprecated - kept for backward compat, mirrors first uploadedImage */
  uploadedImageData: string | null
  mentionDropOpen: boolean
  conversationRounds: ConversationRound[]
  roundCounter: number
  isGenerating: boolean
  uploadProgress: UploadProgress
  selectedRatio: string
  selectedRes: string
  selectedDuration: string
  selectedStyle: string
  selectedStyleLabel: string
  selectedCamera: string
  selectedCameraLabel: string
  selectedModel: string
  selectedModelLabel: string
  pexelsData: PexelsVideo[]
  currentPexelsQuery: string
  currentPexelsPage: number
  currentTab: MaterialTabKey
  pexelsLoading: boolean
  /** 当前展开的下层面板 id，如 model / ratio */
  openDropdown: string | null
  currentSuggestion: string
  ghostHtml: string
  showTabHint: boolean
  isEnhancing: boolean
  watermark: boolean
  materialDetailOpen: boolean
  currentDetailIndex: number
  detailVideo: DetailVideo | null
  videoModalOpen: boolean
  videoModalUrl: string
  promptText: string
  /** 图片校验等错误提示（运行时写入） */
  errorMsg?: string
  // Session management
  currentSessionId: string | null
  sessions: VideoSession[]
  sessionsLoading: boolean
  /** 当前会话内是否点击过「重新编辑」 */
  sessionUseEditAgain: boolean
  /** 当前会话内是否点击过「再次生成」 */
  sessionUseGenerateAgain: boolean
  /** 当前会话是否通过模板（做同款/一键同款）发起 */
  sessionUseHotPrompt: boolean
  /** 当前会话内发起的生成轮次数 */
  sessionGenerateRounds: number
  /** 当前会话内成功生成的视频数 */
  sessionSucceededVideos: number
  quota: QuotaInfo
  /** 活动资格；默认 true，接口异常时不阻塞 */
  activityEligible: boolean
  activityMessage: string
}

export interface AuthState {
  checked: boolean
  loggedIn: boolean
  nickname: string
  avatar: string
  userid: number
  account: string
}

// --- Session Management Types ---

export interface VideoSession {
  id: string
  user_id: string
  title: string
  status: string
  created_at: string
  updated_at: string
  latest_round_status?: string
  latest_video_url?: string
  latest_cover_url?: string
  round_count?: number
}

export interface VideoRound {
  id: string
  session_id: string
  prompt: string
  actual_prompt: string
  params: string // JSON string
  provider_task_id: string
  status: string // analyzing, specify_target, not_video, pending, running, succeeded, failed, cancelled
  video_url: string
  cover_url: string
  image_ref: string
  error_message: string
  model: string
  intent: string
  reference_video_url: string
  cdn_status?: string
  downgrade_status?: string
  downgrade_notice?: string
  created_at: string
  updated_at: string
}

export interface SessionWithRounds extends VideoSession {
  rounds: VideoRound[]
}

export interface SessionListResponse {
  sessions: VideoSession[]
}

export interface RoundListResponse {
  rounds: VideoRound[]
}

export interface CreateRoundRequest {
  prompt: string
  duration: string
  size: string
  ratio?: string
  reference_images?: string[]
  input_videos?: InputVideo[]
  audio_urls?: string[]
  model?: string
  watermark?: boolean
}

/** API 错误（与 fetch 处理逻辑一致） */
export interface ApiRequestError extends Error {
  serverError?: string
  errorCode?: string
}

export interface GenerateRequestOptions {
  selectedCamera: string
  selectedStyle: string
  /** 不传时与原版一致，请求体 model 为空字符串 */
  selectedModel?: string
  cameraPrefixes: Record<string, string>
  styleSuffixes: Record<string, string>
  cameraFixed: Record<string, boolean>
}

export interface TaskPollResponse {
  status?: string
  error?: string
  task_id?: string
  video_url?: string
  cover_url?: string
  actual_prompt?: string
  cdn_status?: string
  downgrade_status?: string
  downgrade_notice?: string
  progress?: number
  content?: Array<{
    type?: string
    video_url?: { url?: string } | string
    url?: string
  }>
  output?: { video_url?: string }
  result?: { video_url?: string }
}

export interface GenerateResponse {
  task_id?: string
  id?: string
}

export interface EnhancePromptResponse {
  enhanced?: string
}

/** 本地 Demo 素材（demoMaterials.json） */
export interface DemoMaterial {
  id: string
  title: string
  text: string
  image: string
  video: string
  image_url?: string
  video_url?: string
  reference_urls?: string[]
  /** @deprecated 上传 CDN 后已移除 */
  image_base64?: string
  /** @deprecated 上传 CDN 后已移除 */
  references_base64?: string[]
}

export interface PexelsListResponse {
  videos?: PexelsVideo[]
}
